<script setup lang="ts">
import { computed, ref } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import { MAX_ARTICLE_MEMO_LENGTH } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import InputTag from '@/components/common/atoms/input-tag.vue';
import TextCounter from '@/components/common/molecules/text-counter.vue';
import GroupSelect from '@/components/modals/group-select.vue';
import { AdpDocument, Group, ThemeList } from '@/types';
import { useGroupTags } from '@/utils/swr';
import { userSession } from '@/utils/userSession';

interface Props {
  adpDocument: AdpDocument;
  group?: Group;
  // モーダルの記事表示の閲覧のため
  // TODO: 閲覧処理は親のコンポーネントに移す; AdpDocumentCard をプレビューカードなどにリファクタする
  theme?: ThemeList;
  pageName: PageName;
  feature?: Feature;
}

const props = defineProps<Props>();

type Submit = { group: Group; tags?: string[]; memo?: string };
const emit = defineEmits<{
  close: [];
  submit: [Submit];
}>();

const selectedTags = ref<string[]>([]);
const selectedGroup = ref<Group | undefined>(undefined);
const editingMemo = ref('');
const isMarkedGroup = ref(false);

const initialGroupId = props.group?.id ?? userSession.getLastUsedGroupId();

const groupIdForFetchTags = computed(() => {
  return selectedGroup.value?.id ?? initialGroupId;
});
const { data } = useGroupTags(groupIdForFetchTags);

const setSelectedGroup = async (group: Group) => {
  selectedGroup.value = group;
  const groupNames = props.adpDocument.group_marks.map(g => g.group_name);
  isMarkedGroup.value = groupNames.includes(group.name);
};

const hideDialog = () => {
  emit('close');
  return true;
};

const submit = async () => {
  if (!selectedGroup.value) return;

  if (initialGroupId !== selectedGroup.value.id) {
    userSession.setLastUsedGroupId(selectedGroup.value.id);
  }
  emit('submit', {
    group: selectedGroup.value,
    tags: selectedTags.value,
    memo: editingMemo.value,
  });
};
</script>

<template>
  <DgrDialog
    title="グループマーク"
    :open="true"
    :close-interceptor-handler="hideDialog"
    @cancel="hideDialog"
    :close-on-escape="false"
  >
    <template #description>
      <div class="o-body c-text-regular--m">
        <div class="o-group-select">
          <GroupSelect
            @selected="setSelectedGroup"
            options-max-height="160px"
            :is-error="isMarkedGroup"
            :initial-group-id="initialGroupId"
          ></GroupSelect>
          <div class="o-group-select__text c-text-regular--m">
            にグループマーク
          </div>
        </div>
        <div class="o-group-select__error" v-if="isMarkedGroup">
          すでにこの記事は指定したグループでマークされています
        </div>
        <div class="spacing-12"></div>
        <AdpDocumentCard
          :adp-document="adpDocument"
          :theme-id="theme ? theme.id : undefined"
          :show-comment="false"
          :show-user-action="false"
          :page-name="pageName"
          :feature="feature"
        ></AdpDocumentCard>
        <div class="spacing-08"></div>
        <InputTag
          :tag-list="data?.tags"
          v-model="selectedTags"
          placeholder="グループタグを入力後、改行でグループタグを追加"
          :is-editing="true"
          :is-group="true"
        ></InputTag>
        <div class="c-formBlock">
          <div class="c-formBlock__label">グループメモ</div>
          <textarea
            class="o-article-memo c-textArea"
            v-model.trim="editingMemo"
            :maxlength="MAX_ARTICLE_MEMO_LENGTH"
            placeholder="グループメモを入力"
            @keydown.ctrl.enter="submit"
            @keydown.meta.enter="submit"
          ></textarea>
          <TextCounter
            :text="editingMemo"
            :max-length="MAX_ARTICLE_MEMO_LENGTH"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <button
        class="o-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: isMarkedGroup }"
        :disabled="isMarkedGroup"
        @click="submit"
      >
        グループマーク
      </button>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.o-group-select {
  display: flex;
  align-items: center;
  .o-group-select__text {
    margin-left: 8px;
  }
}
.o-group-select__error {
  color: $color-orange1000;
}
.o-article {
  display: flex;
  align-items: center;
  border-radius: 4px;
  border: 1px solid $color-gray400;
  .o-article-info {
    width: 484px;
    padding: 16px;
    box-sizing: border-box;
    .o-article-info__title {
      font-family: Noto Sans CJK JP;
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
    }
    .o-article-info__media {
      color: $color-gray600;
    }
  }
  .o-article-image {
    width: 100px;
    height: 100px;
    object-fit: cover;
    object-position: 50% 25%;
    border-left: 1px solid $color-gray400;
    border-radius: 0 3px 3px 0;
  }
}
.o-article-memo {
  height: 140px;
  resize: none;
  outline: none;
}
textarea::placeholder {
  color: $color-gray600;
}

.o-body {
  width: 616px;
}
.o-footer {
  margin-top: -16px;
}
</style>
